import React, { useRef, useEffect } from 'react';
import addClass from 'dom-helpers/addClass';
import removeClass from 'dom-helpers/removeClass';
import getWidth from 'dom-helpers/width';
import scrollbarSize from 'dom-helpers/scrollbarSize';
import { inRange } from 'react-big-calendar/lib/utils/eventLevels';
import { isSelected } from 'react-big-calendar/lib/utils/selection';
import RbcAgenda from 'react-big-calendar/lib/Agenda';

/**
 * Come la vista Agenda di react-big-calendar, ma se l'evento ha `agendaAccessFooter`
 * viene aggiunta una seconda riga full-width (colSpan 2 o 3).
 * agendaHideEventColumn: solo colonne Data + Ora (niente colonna Evento).
 */
function CustomAgendaView({
  accessors,
  components,
  date,
  events,
  getters,
  length,
  localizer,
  onDoubleClickEvent,
  onSelectEvent,
  selected,
  agendaHideEventColumn = false,
}) {
  const headerRef = useRef(null);
  const dateColRef = useRef(null);
  const timeColRef = useRef(null);
  const contentRef = useRef(null);
  const tbodyRef = useRef(null);
  const prevColWidthsRef = useRef([null, null]);

  const timeRangeLabel = (day, event) => {
    let labelClass = '';
    const TimeComponent = components.time;
    let label = localizer.messages.allDay;
    const end = accessors.end(event);
    const start = accessors.start(event);
    if (!accessors.allDay(event)) {
      if (localizer.eq(start, end)) {
        label = localizer.format(start, 'agendaTimeFormat');
      } else if (localizer.isSameDate(start, end)) {
        label = localizer.format({ start, end }, 'agendaTimeRangeFormat');
      } else if (localizer.isSameDate(day, start)) {
        label = localizer.format(start, 'agendaTimeFormat');
      } else if (localizer.isSameDate(day, end)) {
        label = localizer.format(end, 'agendaTimeFormat');
      }
    }
    if (localizer.gt(day, start, 'day')) labelClass = 'rbc-continues-prior';
    if (localizer.lt(day, end, 'day')) labelClass += ' rbc-continues-after';
    return (
      <span className={labelClass.trim()}>
        {TimeComponent ? (
          <TimeComponent event={event} day={day} label={label} />
        ) : (
          label
        )}
      </span>
    );
  };

  const renderDay = (day, eventsInRange, dayKey) => {
    const Event = components.event;
    const AgendaDate = components.date;

    const dayEvents = eventsInRange.filter((e) =>
      inRange(e, localizer.startOf(day, 'day'), localizer.endOf(day, 'day'), accessors, localizer)
    );

    const dayHasAccessFooter = dayEvents.some((e) => e.agendaAccessFooter != null);
    const totalRowsMainOnly = dayEvents.length;

    return dayEvents.flatMap((event, idx) => {
      const title = accessors.title(event);
      const end = accessors.end(event);
      const start = accessors.start(event);
      const userProps = getters.eventProp(event, start, end, isSelected(event, selected));
      const dateLabel = idx === 0 && localizer.format(day, 'agendaDateFormat');

      /*
       * Con rowSpan sulla Data, una riga successiva con colSpan={3} non può partire dalla colonna 1
       * (la cella Data occupa ancora le righe sotto). Se c'è almeno un pulsante Accedi:
       * niente rowSpan; ogni riga principale ha 3 celle (data piena solo su idx 0, poi celle vuote).
       * La riga del pulsante usa colSpan su tutte le colonne della tabella.
       */
      let dateCell;
      if (dayHasAccessFooter) {
        dateCell =
          idx === 0 ? (
            <td className="rbc-agenda-date-cell">
              {AgendaDate ? <AgendaDate day={day} label={dateLabel} /> : dateLabel}
            </td>
          ) : (
            <td className="rbc-agenda-date-cell rbc-agenda-date-cell--continuation" aria-hidden="true" />
          );
      } else {
        dateCell =
          idx === 0 ? (
            <td rowSpan={totalRowsMainOnly} className="rbc-agenda-date-cell">
              {AgendaDate ? <AgendaDate day={day} label={dateLabel} /> : dateLabel}
            </td>
          ) : null;
      }

      const fullWidthColSpan = agendaHideEventColumn ? 2 : 3;

      const mainRow = (
        <tr
          key={`${dayKey}_${idx}`}
          className={userProps.className}
          style={{
            ...userProps.style,
            ...(agendaHideEventColumn ? { cursor: 'pointer' } : {}),
          }}
          onClick={agendaHideEventColumn ? (e) => onSelectEvent && onSelectEvent(event, e) : undefined}
          onDoubleClick={
            agendaHideEventColumn ? (e) => onDoubleClickEvent && onDoubleClickEvent(event, e) : undefined
          }
        >
          {dateCell}
          <td className="rbc-agenda-time-cell">{timeRangeLabel(day, event)}</td>
          {!agendaHideEventColumn ? (
            <td
              className="rbc-agenda-event-cell"
              onClick={(e) => onSelectEvent && onSelectEvent(event, e)}
              onDoubleClick={(e) => onDoubleClickEvent && onDoubleClickEvent(event, e)}
            >
              {Event ? <Event event={event} title={title} /> : title}
            </td>
          ) : null}
        </tr>
      );

      if (event.agendaAccessFooter == null) {
        return [mainRow];
      }

      return [
        mainRow,
        <tr key={`${dayKey}_${idx}_access`} className="rbc-agenda-access-button-row">
          <td
            colSpan={fullWidthColSpan}
            className="rbc-agenda-access-full-cell"
            onClick={(e) => e.stopPropagation()}
          >
            {event.agendaAccessFooter}
          </td>
        </tr>,
      ];
    });
  };

  const isNarrowViewport = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767.98px)').matches;
  };

  const adjustHeader = () => {
    if (!tbodyRef.current || !contentRef.current || !headerRef.current) return;
    const header = headerRef.current;
    const firstRow = tbodyRef.current.firstChild;
    if (!firstRow || firstRow.children.length < 2) return;
    if (isNarrowViewport()) {
      prevColWidthsRef.current = [null, null];
      if (dateColRef.current) dateColRef.current.style.width = '';
      if (timeColRef.current) timeColRef.current.style.width = '';
    } else {
      const w0 = getWidth(firstRow.children[0]);
      const w1 = getWidth(firstRow.children[1]);
      const [pw0, pw1] = prevColWidthsRef.current;
      if (w0 !== pw0 || w1 !== pw1) {
        prevColWidthsRef.current = [w0, w1];
        if (dateColRef.current) dateColRef.current.style.width = `${w0}px`;
        if (timeColRef.current) timeColRef.current.style.width = `${w1}px`;
      }
    }
    const isOverflowing = contentRef.current.scrollHeight > contentRef.current.clientHeight;
    if (isOverflowing) {
      addClass(header, 'rbc-header-overflowing');
      header.style.marginRight = isNarrowViewport() ? '0' : `${scrollbarSize()}px`;
    } else {
      removeClass(header, 'rbc-header-overflowing');
      header.style.marginRight = '';
    }
  };

  useEffect(() => {
    const mql = typeof window !== 'undefined' ? window.matchMedia('(max-width: 767.98px)') : null;
    const onMql = () => {
      prevColWidthsRef.current = [null, null];
    };
    if (mql) mql.addEventListener('change', onMql);
    return () => mql && mql.removeEventListener('change', onMql);
  }, []);

  useEffect(() => {
    adjustHeader();
  });

  const messages = localizer.messages;
  const rangeEnd = localizer.add(date, length, 'day');
  const range = localizer.range(date, rangeEnd, 'day');

  let filtered = events.filter((event) =>
    inRange(event, localizer.startOf(date, 'day'), localizer.endOf(rangeEnd, 'day'), accessors, localizer)
  );
  filtered = filtered.sort((a, b) => +accessors.start(a) - +accessors.start(b));

  return (
    <div className="rbc-agenda-view">
      {filtered.length !== 0 ? (
        <>
          <table ref={headerRef} className="rbc-agenda-table">
            <thead>
              <tr>
                <th className="rbc-header" ref={dateColRef}>
                  {messages.date}
                </th>
                <th className="rbc-header" ref={timeColRef}>
                  {messages.time}
                </th>
                {!agendaHideEventColumn ? (
                  <th className="rbc-header">{messages.event}</th>
                ) : null}
              </tr>
            </thead>
          </table>
          <div className="rbc-agenda-content" ref={contentRef}>
            <table className="rbc-agenda-table">
              <tbody ref={tbodyRef}>{range.flatMap((day, idx) => renderDay(day, filtered, idx))}</tbody>
            </table>
          </div>
        </>
      ) : (
        <span className="rbc-agenda-empty">{messages.noEventsInRange}</span>
      )}
    </div>
  );
}

CustomAgendaView.defaultProps = RbcAgenda.defaultProps;

CustomAgendaView.range = RbcAgenda.range;
CustomAgendaView.navigate = RbcAgenda.navigate;
CustomAgendaView.title = RbcAgenda.title;

export default CustomAgendaView;
